import { Widgets } from '@mui/icons-material';
import { Box, Typography } from '@mui/material';
import React, { Component, ErrorInfo, ReactNode } from 'react';

type ErrorBoundaryType = 'section' | 'page' | 'component';

interface ErrorBoundaryProps {
  type?: ErrorBoundaryType;
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  static defaultProps = {
    type: 'component' as ErrorBoundaryType,
  };

  state: ErrorBoundaryState = {
    hasError: false,
    error: undefined,
  };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // You can log the error to an error reporting service here
    // console.error(error, info);
  }

  componentDidUpdate() {
    if (import.meta.hot) {
      import.meta.hot.accept(() => {
        this.setState({ hasError: false, error: undefined });
      });
    }
  }

  render() {
    const { hasError, error } = this.state;
    const { type = 'component', children } = this.props;

    if (hasError) {
      return (
        <Box
          display="flex"
          flexDirection={'column'}
          justifyContent="center"
          alignItems="center"
          height="400px"
          width="100%"
          textAlign="center"
          sx={{ color: 'text.secondary' }}
        >
          <Widgets sx={{ height: 100, width: 'auto' }} />
          <Typography variant="h6">{type} not available</Typography>
          {import.meta.env.DEV && error && (
            <Typography variant="body2" color="error" sx={{ mt: 2 }}>
              {error.message}
            </Typography>
          )}
        </Box>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
